import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react'
import { Grid } from '@mui/material'
import YellowButton from './YellowButton'
import { DEFAULT_THEME } from '../utils/useThemes'

const styles = {
    canvas: {
        border: `1px solid ${DEFAULT_THEME.palette.lightGray || '#e0e0e0'}`,
        borderRadius: 6,
        backgroundColor: '#fff'
    }
}

// Lightweight signature pad without external deps
const SignaturePad = forwardRef(({ width = 480, height = 180, onChange, disabled = false, showClearButton = true }, ref) => {
    const canvasRef = useRef(null)
    const ctxRef = useRef(null)
    const drawing = useRef(false)
    const lastPoint = useRef({ x: 0, y: 0 })
    const [hasDrawing, setHasDrawing] = useState(false)

    useImperativeHandle(ref, () => ({
        clear, getDataURL, getBlob,
    }))

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.lineWidth = 2
        ctx.strokeStyle = '#111'
        ctxRef.current = ctx
    }, [])

    const getRelativePos = (e) => {
        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()
        const clientX = e.touches ? e.touches[0].clientX : e.clientX
        const clientY = e.touches ? e.touches[0].clientY : e.clientY
        return { x: clientX - rect.left, y: clientY - rect.top }
    }

    const start = (e) => {
        if (disabled) return
        drawing.current = true
        lastPoint.current = getRelativePos(e)
    }
    const move = (e) => {
        if (!drawing.current || disabled) return
        const p = getRelativePos(e)
        const ctx = ctxRef.current
        ctx.beginPath()
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
        ctx.lineTo(p.x, p.y)
        ctx.stroke()
        lastPoint.current = p
        if (!hasDrawing) setHasDrawing(true)
        if (onChange) onChange(true)
        e.preventDefault()
    }
    const end = () => { drawing.current = false }

    const clear = () => {
        const canvas = canvasRef.current
        const ctx = ctxRef.current
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        setHasDrawing(false)
        if (onChange) onChange(false)
    }

    const getDataURL = () => {
        const canvas = canvasRef.current
        return canvas.toDataURL('image/png')
    }
    const getBlob = () => new Promise((resolve) => {
        const canvas = canvasRef.current
        canvas.toBlob((blob) => resolve(blob), 'image/png')
    })

    return (
        <Grid container direction="column" alignItems="flex-start">
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{ ...styles.canvas, width, height }}
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
            />
            {showClearButton ? (
                <Grid item sx={{ mt: '0.75rem' }}>
                    <YellowButton disabled={!hasDrawing} onClick={clear}>{'Temizle'}</YellowButton>
                </Grid>
            ) : null}
        </Grid>
    )
})

export default SignaturePad


